import { CSSProperties } from 'react'
import { none, Option } from 'widgets-for-react'
import { pushAanZetDataLayer } from '../AanZet/custom_components/components/datalayer'
import { AanZetErrorElement } from "../AanZet/custom_components/components/error"
import { AanZetEventBlocks, fetchEventsOverviewJumboAanZet, maybeRenderAanZetCmsPage, renderAanZetJumbo } from '../AanZet/Features/Events'
import { pushDNBDataLayer, pushTranslateAction as pushTranslateActionDNB } from '../DeNieuweBibliotheek/custom_components/components/datalayer'
import { DNBErrorElement } from "../DeNieuweBibliotheek/custom_components/components/error"
import { pushTranslateAction as pushTranslateActionWhiteLabel } from '../Generic/custom_components/components/datalayer'
import { GenericErrorElement } from "../Generic/custom_components/components/error"
import {
  fetchEventsOverviewJumboGeneric,
  GenericEventBlocks,
  maybeRenderCmsPage,
  renderGenericJumbo
} from '../Generic/Features/Events'
import { defaultEventsConfig, EventsConfig } from './components/Events/EventState'
import { PageType, showOnHome, showOnHomeAndActivities } from './features/showAnnouncementOnPage'
import { DataLayerObject, pushDataLayer } from './utils/datalayer'
import { fromNullable, valueOrExcept } from './utils/option'

export type Switch<T = 'on'> = T | 'off'

export interface Features {
  automatedTranslations: 'off' | { iconFill: CSSProperties['color']; pushDataLayerEvent: (label: string) => void }
  branchLocalization: Switch
  showAnnouncementOnPage: (page: PageType) => boolean
  privacyStatementUrl: string
  events: Switch<EventsConfig>
  pushDataLayer: (data: DataLayerObject) => void
  error: JSX.Element
}

const vbp: Features = {
  automatedTranslations: {
    iconFill: '#090b09',
    pushDataLayerEvent: (label: string) => pushTranslateActionWhiteLabel(label)
  },
  branchLocalization: 'off',
  showAnnouncementOnPage: showOnHome,
  privacyStatementUrl: '/privacyverklaring',
  events: 'off',
  pushDataLayer: (data: DataLayerObject) => pushDataLayer({ event: 'event', ...data }),
  error: GenericErrorElement
}

const aanzet: Features = {
  ...vbp,
  automatedTranslations: 'off',
  branchLocalization: 'off',
  events: {
    ...defaultEventsConfig,
    loadJumbo: fetchEventsOverviewJumboAanZet,
    Jumbo: renderAanZetJumbo,
    renderCustomCmsPage: maybeRenderAanZetCmsPage,
    Blocks: AanZetEventBlocks
  },
  pushDataLayer: pushAanZetDataLayer,
  error: AanZetErrorElement
}

const bkw: Features = {
  ...vbp,
  events: {
    ...defaultEventsConfig,
    loadJumbo: fetchEventsOverviewJumboGeneric('small'),
    Jumbo: renderGenericJumbo,
    renderCustomCmsPage: maybeRenderCmsPage,
    Blocks: GenericEventBlocks
  }
}

const dnb: Features = {
  ...vbp,
  automatedTranslations: { iconFill: '#ffffff', pushDataLayerEvent: (label: string) => pushTranslateActionDNB(label) },
  privacyStatementUrl: '/privacy',
  // events: {
  //   ...defaultEventsConfig,
  //   loadJumbo: fetchEventsOverviewJumboDNB,
  //   Jumbo: renderDNBJumbo,
  //   renderCustomCmsPage: maybeRenderDNBCmsPage,
  //   Blocks: DNBEventBlocks
  // },
  pushDataLayer: pushDNBDataLayer,
  error: DNBErrorElement
}

const flevo: Features = {
  ...vbp,
  automatedTranslations: 'off',
  showAnnouncementOnPage: showOnHomeAndActivities,
  privacyStatementUrl: '/privacy',
  // events: {
  //   ...defaultEventsConfig,
  //   loadJumbo: fetchEventsOverviewJumboGeneric('small'),
  //   Jumbo: renderGenericJumbo,
  //   renderCustomCmsPage: maybeRenderCmsPage,
  //   Blocks: GenericEventBlocks
  // },
}

const rdam: Features = {
  ...vbp
}

const twente: Features = {
  ...vbp,
  automatedTranslations: 'off',
  events: {
    ...defaultEventsConfig,
    overviewPage: {
      ...defaultEventsConfig.overviewPage,
      locationFilter: {
        kind: 'location',
        heading: 'Waar?',
        defaultLabel: 'Alle locaties'
      }
    },
    loadJumbo: fetchEventsOverviewJumboGeneric('large'),
    Jumbo: renderGenericJumbo,
    renderCustomCmsPage: maybeRenderCmsPage,
    Blocks: GenericEventBlocks
  }
}

// Define LibraryIDs and guard clause
const libraryIDs = ['vbp', 'bkw', 'flevo', 'twente', 'dnb', 'aanzet', 'rdam'] as const
export type LibraryID = typeof libraryIDs[number]
const isLibraryID = (id: string): id is LibraryID => libraryIDs.includes(id as LibraryID)

// Define implementations of Library for each LibraryID
type FeatureSets = { [library in LibraryID]: Features }
const libraryCustomizations: FeatureSets = { vbp, aanzet, bkw, flevo, twente, dnb, rdam }

// Set application wide Library at runtime
let _features: Option<Features> = none()
export const setFeatures = (id: string) => {
  if (!isLibraryID(id)) throw new TypeError(`Library ID must be one of: ${libraryIDs.join(', ')}`)
  _features = fromNullable(libraryCustomizations[id])
}

// Get Features at runtime
export const features = (): Features =>
  valueOrExcept(
    _features,
    'Features have not yet been set. Make sure to call features() within the context of the React Application component.'
  )
